import { useState } from 'react';
import {
  Button,
  Grid,
  CircularProgress,
  Box,
  ImageListItem,
  ImageList,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ImageListItemBar,
} from '@mui/material';
import { uploadFiles } from 'src/api/files/uploadFiles';
import { listFiles } from 'src/api/files/listFiles';
import Iconify from '../iconify';

interface AssetsDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (assetUrl: string) => void;
}

type ShowAssetsType = 'image_library' | null;

export const AssetsDialog = ({
  open,
  onClose,
  onSuccess,
}: AssetsDialogProps) => {
  const [loading, setLoading] = useState(false);
  const [showAssetsType, setShowAssetsType] = useState<ShowAssetsType>(null);
  const [imageLibraryFiles, setImageLibraryFiles] = useState<
    { blobName: string; blobUrl: string }[]
  >([]);

  const onCancel = () => {
    setShowAssetsType(null);
    onClose();
  };

  const handleUploadFiles = async (event: any) => {
    if (event.target.files.length > 0) {
      setLoading(true);
      const files = Array.from(event.target.files) as File[];

      try {
        const response = await uploadFiles(files);
        const link = response.urls[0];
        setLoading(false);
        addAssetHandler(link);
      } catch (error) {
        console.error(error);
        setLoading(false);
      }
    }
  };

  const addAssetHandler = (assetUrl: string) => {
    onSuccess(assetUrl);
  };

  const loadImageLibraryFiles = () => {
    setLoading(true);

    listFiles()
      .then((response) => {
        setImageLibraryFiles(response.files);
        setShowAssetsType('image_library');
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        aria-labelledby='alert-dialog-title'
        aria-describedby='alert-dialog-description'
      >
        <DialogTitle id='alert-dialog-title'>Add Asset</DialogTitle>
        <DialogContent sx={{ minWidth: 600 }}>
          {loading && (
            <Box
              sx={{ display: 'flex', justifyContent: 'center' }}
              style={{ padding: '100px 200px' }}
            >
              <CircularProgress />
            </Box>
          )}

          {showAssetsType === null && !loading && (
            <Grid container>
              <Grid item sx={{ cursor: 'pointer', p: 1 }}>
                <Grid container>
                  <Grid
                    xs={12}
                    item
                    sx={{
                      height: 120,
                      width: 40,
                      textAlign: 'center',
                      p: 4,
                    }}
                  >
                    <img
                      alt='image library'
                      src='/assets/icons/navbar/image-outline.svg'
                      onClick={() => loadImageLibraryFiles()}
                    />
                  </Grid>
                  <Grid
                    item
                    sx={{ fontSize: 14, textAlign: 'center', width: '100%' }}
                  >
                    Image Library
                  </Grid>
                </Grid>
              </Grid>
              <Grid item sx={{ cursor: 'pointer', p: 1 }}>
                <Grid container>
                  <Grid
                    xs={12}
                    item
                    sx={{
                      height: 120,
                      width: 50,
                      textAlign: 'center',
                      p: 4,
                    }}
                  >
                    <input
                      accept='image/*'
                      type='file'
                      id='select-icon'
                      style={{ display: 'none' }}
                      onChange={async (e) => await handleUploadFiles(e)}
                    />
                    <label htmlFor='select-icon'>
                      <Iconify
                        icon='eva:plus-circle-outline'
                        sx={{
                          width: 50,
                          height: 50,
                          cursor: 'pointer',
                          marginTop: 2,
                          marginBottom: 2,
                        }}
                      />
                    </label>
                  </Grid>
                  <Grid
                    sx={{ fontSize: 14, textAlign: 'center', width: '100%' }}
                  >
                    Upload Image
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}

          {showAssetsType === 'image_library' && !loading && (
            <Grid container>
              <ImageList cols={3}>
                {imageLibraryFiles.map((item, index) => (
                  <ImageListItem
                    key={index}
                    onClick={() => addAssetHandler(item.blobUrl)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <img
                      src={item.blobUrl}
                      srcSet={item.blobUrl}
                      alt='library'
                      loading='lazy'
                    />
                    <ImageListItemBar title={item.blobName} subtitle={''} />
                  </ImageListItem>
                ))}
              </ImageList>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => onCancel()}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
